import React, { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import UserProgressContext from "../../userProgressContext";
import SoundManager from "../../services/SoundManager";
import { shake } from "../../utils/motion.config";

const MCQ = ({ question, options, onCorrectAnswer, onWrongAnswer }) => {
  const {
    audioMuted,
    firstAttemptKeys,
    addFirstAttemptKey,
    localProgress,
    setNextButtonLocked,
  } = useContext(UserProgressContext);

  const [selectedOption, setSelectedOption] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [isAnswerCorrect, setIsAnswerCorrect] = useState(null);
  const [isShaking, setIsShaking] = useState(false);

  // Lock the Next button while this MCQ step is active
  useEffect(() => {
    setNextButtonLocked(true);
    return () => {
      setNextButtonLocked(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOptionClick = (isCorrect, feedbackText) => {
    const isFirstAttempt = !firstAttemptKeys.has(String(localProgress));
    if (isFirstAttempt) {
      addFirstAttemptKey(String(localProgress));
    }

    setSelectedOption(isCorrect);
    setFeedback(feedbackText);

    if (!isCorrect) {
      SoundManager.playSfx('wrong-option-click');
      setIsAnswerCorrect(false);
      setIsShaking(true);
      onWrongAnswer && onWrongAnswer();
    } else {
      SoundManager.playSfx('right-option-click');
      setNextButtonLocked(false);
      setIsAnswerCorrect(true);
      onCorrectAnswer && onCorrectAnswer(isFirstAttempt);
    }
  };

  const handleTryAgainClick = () => {
    SoundManager.playSfx('game-retro-click');
    setTimeout(() => {
      setSelectedOption(null);
      setFeedback("");
      setIsAnswerCorrect(null);
    }, 100);
  };

  return (
    <div className="p-8 rounded-lg bg-white text-black w-full h-full flex flex-col">
      <motion.div
        animate={isShaking ? shake : {}}
        onAnimationComplete={() => setIsShaking(false)}
      >
        <h2 className="text-xl">{question}</h2>
        <div className="mt-6 flex flex-col gap-4 p-0 md:p-4">
          {options.map((option) => (
            <button
              key={option.id}
              onClick={() => handleOptionClick(option.isCorrect, option.feedback)}
              className={`${
                selectedOption !== null
                  ? option.isCorrect
                    ? "bg-green-500"
                    : "bg-red-500"
                  : "hover:bg-indigo-300"
              } block border border-indigo-500 rounded-lg px-4 md:px-8 py-4 text-black text-sm md:text-lg cursor-pointer transition-all ease-in delay-100`}
              disabled={selectedOption !== null}
            >
              {option.text}
            </button>
          ))}
        </div>
      </motion.div>
      {feedback && (
        <div className={isAnswerCorrect === false ? "animate-pulseShadow rounded p-2" : ""}>
          <p className="mt-6 font-bold text-base md:text-lg">{feedback}</p>
        </div>
      )}
      {isAnswerCorrect === false && (
        <div className="flex justify-center items-center mt-4">
          <button
            onClick={handleTryAgainClick}
            className="bg-indigo-400 text-white py-1 px-4 rounded-lg text-sm md:text-lg hover:bg-indigo-500"
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
};

export default MCQ;
